import { createContext } from 'react';

import {
  getAmount,
  RefFiFunctionCallOptions,
  getGas,
  REF_FARM_BOOST_CONTRACT_ID,
} from '../services/near';
import { scientificNotationToString } from './numbers';
import { ACCOUNT_ID_KEY } from '../context/WalletSelectorContext';
import getConfig from '../services/config';
import {
  TRANSACTION_WALLET_TYPE,
  TRANSACTION_STATE,
} from '../components/layout/transactionTipPopUp';
import { WalletSelector } from '@near-wallet-selector/core';

const SENDER_WALLET_SIGNEDIN_STATE_KEY = 'SENDER_WALLET_SIGNEDIN_STATE_VALUE';

export const walletsRejectError = [
  'User reject',
  'User rejected the signature request',
  'Invalid message. Only transactions can be signed',
  'Ledger device: Condition of use not satisfied (denied by the user?) (0x6985)',
  'User cancelled the action',
  'User closed the window before completing the action',
];

export const extraWalletsError = [
  "Couldn't open popup window to complete wallet action",
];

const saveSenderLoginRes = (accountId?: string) => {
  localStorage.setItem(
    SENDER_WALLET_SIGNEDIN_STATE_KEY,
    `SENDER_WALLET_SIGNEDIN_STATE_KEY: ${
      accountId || window.near.getAccountId()
    }`
  );
};

export const removeSenderLoginRes = () => {
  localStorage.removeItem(SENDER_WALLET_SIGNEDIN_STATE_KEY);
};

export function addQueryParams(
  baseUrl: string,
  queryParams: {
    [name: string]: string;
  }
) {
  const url = new URL(baseUrl);
  for (let key in queryParams) {
    const param = queryParams[key];
    if (param) url.searchParams.set(key, param);
  }
  return url.toString();
}

const getTransactionHashes = (
  walletResponse: any,
  state: TRANSACTION_STATE
): string[] => {
  if (state === TRANSACTION_STATE.SUCCESS) {
    return walletResponse.response?.map((transactionOutcome: any) => {
      return transactionOutcome.transaction.hash;
    });
  } else {
    return [
      walletResponse?.response?.error?.context?.transactionHash ||
        walletResponse?.response?.error?.transaction_outcome?.id,
    ];
  }
};

const setCallbackUrl = (walletResponse: any) => {
  const state = !walletResponse?.response?.error
    ? TRANSACTION_STATE.SUCCESS
    : TRANSACTION_STATE.FAIL;

  const transactionHashes = getTransactionHashes(walletResponse, state);

  const parsedTransactionHashes = transactionHashes?.join(',');

  const redirectUrl = addQueryParams(
    window.location.origin + window.location.pathname,
    {
      [TRANSACTION_WALLET_TYPE.SENDER_WALLET]: parsedTransactionHashes,
      state: parsedTransactionHashes ? state : '',
    }
  );

  window.location.href = redirectUrl;
};

//@ts-ignore
enum WALLET_TYPE {
  WEB_WALLET = 'near-wallet',
  SENDER_WALLET = 'sender-wallet',
}

enum SENDER_ERROR {
  USER_REJECT = 'User reject',
}

function senderWalletFunc(window: Window) {
  this.requestSignIn = async function (contractId: string) {
    return window.near
      .requestSignIn({
        contractId: contractId || getConfig().REF_FARM_BOOST_CONTRACT_ID,
      })
      .then((signInResult: any) => {
        // Login reject
        if (
          signInResult?.error &&
          signInResult?.error === SENDER_ERROR.USER_REJECT
        ) {
          removeSenderLoginRes();
          window.location.reload();
        }

        // unknown error from near chain
        if (signInResult?.error && signInResult?.error?.type) {
          window.location.href = addQueryParams(window.location.href, {
            signInErrorType: signInResult.error.type,
          });
        }

        // login success
        if (!signInResult?.error) {
          saveSenderLoginRes();
          document
            .getElementsByClassName('sender-login-fail-toast')?.[0]
            ?.setAttribute('style', 'display:none');
        }

        return signInResult;
      });
  };

  this.signOut = function () {
    // removeSenderLoginRes();
    const injectedSenderWallet: any = window?.near;
    const signedInContractSize = injectedSenderWallet?.authData?.allKeys;

    if (
      signedInContractSize &&
      Number(Object.keys(signedInContractSize).length === 1)
    ) {
      return window.near.signOut();
    }

    if (
      signedInContractSize &&
      Object.keys(signedInContractSize).includes('aurora')
    ) {
      return injectedSenderWallet.signOut({
        contractId: 'aurora',
      });
    } else {
      return injectedSenderWallet.signOut({
        contractId: REF_FARM_BOOST_CONTRACT_ID,
      });
    }
  };

  this.requestSignTransactions = async function (transactions: any) {
    if (!window.near.isSignedIn()) {
      await this.requestSignIn(REF_FARM_BOOST_CONTRACT_ID);
    }

    const senderTransaction = transactions.map((transaction: any) => {
      return {
        ...transaction,
        actions: transaction.functionCalls.map((functionCall: any) => ({
          ...functionCall,
          deposit: scientificNotationToString(
            getAmount(functionCall.amount).toString()
          ),
          gas: scientificNotationToString(getGas(functionCall.gas).toString()),
        })),
      };
    });

    return window.near
      .requestSignTransactions({
        transactions: senderTransaction,
      })
      .then((walletResponse: any) => {
        setCallbackUrl(walletResponse);
      });
  };

  this.sendTransactionWithActions = async function (
    receiverId: string,
    functionCalls: RefFiFunctionCallOptions[]
  ) {
    const injectedSenderWallet: any = window?.near;
    if (!window.near.isSignedIn()) {
      await this.requestSignIn(REF_FARM_BOOST_CONTRACT_ID);
    }

    return injectedSenderWallet
      .signAndSendTransaction({
        receiverId,
        actions: functionCalls.map((functionCallOptions) => {
          return {
            ...functionCallOptions,
            deposit: scientificNotationToString(
              getAmount(functionCallOptions.amount).toString()
            ),
            gas: scientificNotationToString(
              getGas(functionCallOptions.gas).toString()
            ),
          };
        }),
      })
      .then((walletResponse: any) => {
        setCallbackUrl(walletResponse);
      });
  };

  this.walletType = WALLET_TYPE.SENDER_WALLET;
}

senderWalletFunc.prototype = window.near;

export const getSenderWallet = (window: Window) => {
  senderWalletFunc.prototype = window.near;

  return new (senderWalletFunc as any)(window);
};

export const getAccountName = (accountId: string) => {
  const [account, network] = accountId.split('.');
  const niceAccountId = `${account.slice(0, 10)}...${network || ''}`;

  return account.length > 10 ? niceAccountId : accountId;
};

export const getCurrentWallet = () => {
  const walletInstance = window.selector;

  if (walletInstance) {
    walletInstance.getAccountId = () => {
      return localStorage.getItem(ACCOUNT_ID_KEY) || '';
    };
  }

  return {
    wallet: walletInstance,
    wallet_type: WALLET_TYPE.WEB_WALLET,
  };
};

export const WalletContext = createContext(null);

export const globalStateReducer = (
  state: { isSignedIn: boolean },
  action: { type: 'signIn' | 'signOut' }
) => {
  switch (action.type) {
    case 'signIn':
      return {
        ...state,
        isSignedIn: true,
      };
    case 'signOut':
      return {
        ...state,
        isSignedIn: false,
      };
  }
};

// export const setCallbackUrl = (res: any) => {
//   const state = !res?.response?.error
//     ? TRANSACTION_STATE.SUCCESS
//     : TRANSACTION_STATE.FAIL;

//   const transactionHashes = getTransactionHashes(res, state);

//   const parsedTransactionHashes = transactionHashes?.join(',');

// const newHref = addQueryParams(
//   window.location.origin + window.location.pathname,
//   {
//     [TRANSACTION_WALLET_TYPE.SENDER_WALLET]: parsedTransactionHashes,
//     state: parsedTransactionHashes ? state : '',
//   }
// );

//   window.location.href = newHref;
// };

// selector.wallet() rejects with "No wallet selected" when the page is signed out.
// Callers that run on load should use this and treat null as disconnected.
export async function getSelectedWallet(
  selector: WalletSelector | null | undefined = typeof window !== 'undefined'
    ? window.selector
    : undefined
) {
  if (!selector?.store?.getState()?.selectedWalletId) return null;
  try {
    return await selector.wallet();
  } catch (err) {
    if ((err as Error)?.message === 'No wallet selected') return null;
    throw err;
  }
}

export const ledgerTipTrigger = async (wallet: WalletSelector) => {
  const handlePopTrigger = () => {
    const popUpElements = document.getElementsByClassName(
      'ledger-transaction-pop-up'
    )?.[0];
    if (popUpElements) {
      popUpElements.setAttribute('style', 'display:flex');
    }
  };

  const selected = await getSelectedWallet(wallet);
  if (selected?.id === 'ledger') {
    handlePopTrigger();
  }
};
